import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'

// Компонент чипа для статуса тикета
const StatusChip = ({ status }) => {
    const getChipStyle = (status) => {
        switch (status) {
            case 'pending':
                return {
                    color: '#6b9beb',
                    border: '1px solid rgba(59, 130, 246, 0.4)'
                }
            case 'reject':
                return {
                    color: '#e67c87',
                    border: '1px solid rgba(220, 53, 69, 0.4)'
                }
            case 'refund':
            default:
                return {
                    color: '#5db87a',
                    border: '1px solid rgba(40, 167, 69, 0.4)'
                }
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return 'Pending'
            case 'reject':
                return 'Rejected'
            case 'refund':
            default:
                return 'Refund'
        }
    }

    return (
        <span style={{
            ...getChipStyle(status),
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
      justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {getStatusText(status)}
        </span>
    )
}

// Компонент DatePicker с кастомным календарем (скопирован из PaymentsTable)
const DatePicker = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [tempDate, setTempDate] = useState(value)

    // Закрытие календаря при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.date-picker-container')) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const formatDisplayDate = (date) => {
        return date.format('MM/DD/YYYY')
    }

    const handleDateSelect = (selectedDate) => {
        setTempDate(selectedDate)
        onChange(selectedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        const clearedDate = dayjs()
        setTempDate(clearedDate)
        onChange(clearedDate)
        setIsOpen(false)
    }

    const handleToday = () => {
        const today = dayjs()
        setTempDate(today)
        onChange(today)
        setIsOpen(false)
    }

    const getCalendarDays = () => {
        const startOfMonth = tempDate.startOf('month')
        const endOfMonth = tempDate.endOf('month')
        const startOfCalendar = startOfMonth.startOf('week')
        const endOfCalendar = endOfMonth.endOf('week')

        const days = []
        let current = startOfCalendar

        while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: current.isSame(tempDate, 'month'),
                isSelected: current.isSame(value, 'day'),
                isToday: current.isSame(dayjs(), 'day')
            })
            current = current.add(1, 'day')
        }

        return days
    }

    const navigateMonth = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'month')
            : tempDate.add(1, 'month')
        setTempDate(newDate)
    }

    const navigateYear = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'year')
            : tempDate.add(1, 'year')
        setTempDate(newDate)
    }
  
    return (
        <div className="form-group date-picker-container" style={{ width: '200px', position: 'relative' }}>
            <label className="form-label">{label}</label>
            <div 
                className="form-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{formatDisplayDate(value)}</span>
                <span style={{ color: 'var(--primary-orange)' }}>📅</span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-md)',
                    boxShadow: 'var(--shadow-card)',
                    marginTop: '4px',
                    width: '280px',
                    minWidth: '280px'
                }}>
                    {/* Заголовок календаря */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--spacing-sm)',
                        gap: '8px'
                    }}>
                        <button
                            onClick={() => navigateYear('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟪
                        </button>
                        <button
                            onClick={() => navigateMonth('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ‹
                        </button>
                        <span style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: '600',
                            color: 'var(--text-light)',
                            flex: 1,
                            textAlign: 'center'
                        }}>
                            {tempDate.format('MMMM YYYY')}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => navigateYear('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟫
                        </button>
                    </div>

                    {/* Дни недели */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} style={{
                                padding: '8px 4px',
                                textAlign: 'center',
                                fontSize: 'var(--text-xs)',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Календарная сетка */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {getCalendarDays().map((day, index) => (
                            <button
                                key={index}
                                onClick={() => handleDateSelect(day.date)}
                                style={{
                                    padding: '8px 4px',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-sm)',
                                    cursor: 'pointer',
                                    background: day.isSelected 
                                        ? 'var(--primary-orange)' 
                                        : day.isToday 
                                            ? 'var(--dark-gray)' 
                                            : 'transparent',
                                    color: day.isSelected 
                                        ? 'var(--white)' 
                                        : !day.isCurrentMonth 
                                            ? 'var(--text-muted)' 
                                            : day.isToday 
                                                ? 'var(--primary-orange)' 
                                                : 'var(--text-light)',
                                    fontWeight: day.isSelected || day.isToday ? '600' : '400',
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = 'var(--dark-gray)'
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = day.isToday ? 'var(--dark-gray)' : 'transparent'
                                    }
                                }}
                            >
                                {day.date.format('D')}
                            </button>
                        ))}
                    </div>

                    {/* Кнопки действий */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 'var(--spacing-sm)',
                        paddingTop: 'var(--spacing-sm)',
                        borderTop: '1px solid var(--light-gray)',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <button
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = 'var(--primary-orange)'
                                e.target.style.color = 'var(--primary-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = 'var(--light-gray)'
                                e.target.style.color = 'var(--text-light)'
                            }}
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleToday}
                            style={{
                                background: 'var(--primary-orange)',
                                border: '1px solid var(--primary-orange)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--white)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--dark-orange)'
                                e.target.style.borderColor = 'var(--dark-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--primary-orange)'
                                e.target.style.borderColor = 'var(--primary-orange)'
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// Компонент пагинации для десктопа
const PaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
            </span>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

const TicketsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [ticketsList, setTicketsList] = React.useState([])
    const [totalTickets, setTotalTickets] = React.useState(0)
    const [selectedTicket, setSelectedTicket] = React.useState(0)

    const [startDate, setStartDate] = React.useState(() => dayjs())
    const [endDate, setEndDate] = React.useState(() => dayjs().endOf('month'))

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы TicketsTable */
            .tickets-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .tickets-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .tickets-table-container,
            :root:not([data-theme="light"]) .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ticketsList?.length) : 0

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD 00:00:00')
            const dateEndFormated = endDate.format('YYYY-MM-DD 23:59:59')

            // Получаем токен один раз
            const token = await getAccessTokenSilently()

            // Получаем текущую страницу
            const ticketsResponse = await props.apiClient.setToken(token).getUserTickets(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setTicketsList(ticketsResponse)

            // Если это первая страница, получаем все записи для подсчета общего количества
            if (page === 0) {
                const allTicketsResponse = await props.apiClient.setToken(token).getUserTickets(dateStartFormated, dateEndFormated, 0, 1000)
                setTotalTickets(allTicketsResponse?.length || 0)
            }
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleSetStartDate = (date) => {
      setStartDate(date)
    }

    const handleSetEndDate = (date) => {
      setEndDate(date)
    }

    const rowClick = (row) => {
      // console.log(row.id)
      setSelectedTicket(row)
    }

    return (
        <div style={{
            width: 'calc(100% - 280px)',
            height: '100vh',
            padding: '8px',
            background: 'var(--black)',
            marginLeft: '280px'
        }}>
            <h1 style={{ 
                marginTop: '8px',
                marginBottom: '8px',
                marginLeft: '0px',
                fontWeight: 600,
                fontSize: 'var(--text-2xl)',
                background: 'var(--gradient-orange)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
            }}>
                Tickets
            </h1>
            
            {/* Фильтры по дате */}
            <div style={{
                background: 'var(--medium-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-lg)',
                marginBottom: 'var(--spacing-lg)',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                gap: 'var(--spacing-lg)',
                alignItems: 'end',
            }}>
                <DatePicker 
                    label="From" 
                    value={startDate} 
                    onChange={handleSetStartDate}
                />
                <DatePicker 
                    label="To" 
                    value={endDate} 
                    onChange={handleSetEndDate}
                />
            </div>
            
            {ticketsList?.length > 0 ? (
                <>
                    <div className="table-container tickets-table-container" style={{ overflowX: 'auto' }}>
                        <table className="table table-compact" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th>Creation Date</th>
                                    <th>Status</th>
                                    <th>Proxy ID</th>
                                    <th>Refund Reason</th>
                                    <th>Description</th>
                                    <th>Close Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketsList?.map((row, index) => (
                                    <tr 
                                        key={index}
                                      onClick={() => rowClick(row)}
                                        style={{
                                            backgroundColor: selectedTicket?.id === row.id ? 'var(--dark-gray)' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <td>{moment(row?.created).format('L LT')}</td>
                                        <td>
                                            <StatusChip status={row?.status} />
                                        </td>
                                        <td>{row?.lease?.id}</td>
                                        <td>{row?.reason}</td>
                                        <td>{row?.message}</td>
                                        <td>{row?.closed === false ? '' : moment(row?.closed).format('L LT')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Навигация */}
                        <PaginationActions 
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={totalTickets}
                            onPageChange={handleChangePage}
                        />
                    </div>
                </>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🎫</div>
                    <h3 className="empty-state-title">No tickets found</h3>
                    <p className="empty-state-description">
                        You don't have any tickets for the selected period.
                    </p>
            </div>
            )}
        </div>
    )
}

export default TicketsTable